import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FolderOpen, Users, Activity, Plus, ArrowRight, Sparkles, LayoutDashboard } from 'lucide-react';
import { api } from '../lib/api';
import { Skeleton, PageHeader } from '../components/ui';
import { formatDistanceToNow } from 'date-fns';

export function Dashboard() {
  const { data: casesData, isLoading: casesLoading } = useQuery({
    queryKey: ['cases', { limit: 5 }],
    queryFn: () => api.cases.list({ limit: 5 }),
  });

  const { data: doctorsStats, isLoading: statsLoading } = useQuery({
    queryKey: ['doctors', 'stats'],
    queryFn: () => api.doctors.stats(),
  });

  const isLoading = casesLoading || statsLoading;

  const stats = [
    {
      name: 'Total Cases',
      value: casesData?.pagination.total || 0,
      icon: FolderOpen,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Care Providers',
      value: doctorsStats?.data.total || 0,
      icon: Users,
      color: 'bg-green-500',
    },
    {
      name: 'Specialties',
      value: doctorsStats?.data.bySpecialty.length || 0,
      icon: Activity,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader
        breadcrumbs={[{ label: 'Case Dashboard', icon: LayoutDashboard }]}
        title="Case Dashboard"
        description="Welcome back"
        actions={
          <Link to="/cases/new" className="btn-primary">
            <Plus className="mr-1.5 h-4 w-4" />
            New Case
          </Link>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${stat.color}`}>
              <stat.icon className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div>
              {isLoading ? (
                <>
                  <Skeleton width={60} height={24} />
                  <Skeleton width={80} height={14} className="mt-1" />
                </>
              ) : (
                <>
                  <p className="text-xl font-semibold text-gray-900">{stat.value.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{stat.name}</p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Cases - Main content */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-900">Recent Cases</h2>
            <Link
              to="/cases"
              className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              View all
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {casesLoading ? (
            <div className="divide-y divide-gray-100">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="py-2.5 -mx-4 px-4">
                  <Skeleton width="70%" height={16} />
                  <Skeleton width="50%" height={12} className="mt-1" />
                </div>
              ))}
            </div>
          ) : casesData?.data && casesData.data.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {casesData.data.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/cases/${caseItem.id}`}
                  className="flex items-center justify-between py-2.5 hover:bg-primary-50 -mx-4 px-4 transition-colors first:-mt-1 group"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 group-hover:text-primary-700">
                      {caseItem.title}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {caseItem.condition}
                    </p>
                  </div>
                  <div className="ml-3 flex items-center gap-2">
                    <span className="text-xs text-gray-400 hidden sm:inline">
                      {formatDistanceToNow(new Date(caseItem.createdAt), { addSuffix: true })}
                    </span>
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                        caseItem.status === 'ACTIVE'
                          ? 'bg-yellow-100 text-yellow-800'
                          : caseItem.status === 'ARCHIVED'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {caseItem.status === 'ACTIVE' ? 'Active' : caseItem.status === 'ARCHIVED' ? 'Archived' : 'Draft'}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <FolderOpen className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">No cases yet</p>
              <Link to="/cases/new" className="btn-primary mt-3 text-sm">
                Create first case
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions Sidebar */}
        <div className="space-y-3">
          <Link
            to="/andy-pro"
            className="card group hover:border-primary-400 transition-all flex items-center gap-3"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 group-hover:bg-primary-200 transition-colors">
              <Sparkles className="h-4 w-4 text-primary-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">Quick Analysis</h3>
              <p className="text-xs text-gray-500">Find care providers without saving</p>
            </div>
          </Link>

          <Link
            to="/cases/new"
            className="card group hover:border-gray-300 transition-all flex items-center gap-3"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 group-hover:bg-blue-200 transition-colors">
              <Plus className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">New Case</h3>
              <p className="text-xs text-gray-500">Create and track a case</p>
            </div>
          </Link>

          <div className="card flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-green-100">
              <Users className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">Doctor Database</h3>
              <p className="text-xs text-gray-500">
                {doctorsStats?.data.total.toLocaleString() || 0} providers
              </p>
            </div>
          </div>

          {/* Tips card */}
          <div className="bg-primary-50 border border-primary-100 rounded-lg p-3">
            <p className="text-xs font-medium text-primary-800 mb-1">Quick tip</p>
            <p className="text-xs text-primary-700">
              Use Quick Analysis for one-off searches. Create a Case to track patient referrals over time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
